import * as React from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { BarChart } from '@mui/x-charts/BarChart'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import InfoIcon from '@mui/icons-material/Info'
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek'
import { format } from 'date-fns'

function getFormattedDate(date, isMobile = false) {
	const day = format(date, 'EEE') // Short day name (e.g., "Tue")
	const month = format(date, 'MMM') // Full month name (e.g., "May")
	const dayOfMonth = format(date, 'd') // Numeric day (e.g., "4")

	// Determine the ordinal suffix
	const suffix = getOrdinalSuffix(dayOfMonth)

	if (isMobile) {
		return `${day}\n${dayOfMonth}${suffix}`
	}

	return `${day}, ${dayOfMonth}${suffix} ${month}`
}

function getOrdinalSuffix(day) {
	const dayInt = parseInt(day, 10)
	if (dayInt % 10 === 1 && dayInt !== 11) return 'st'
	if (dayInt % 10 === 2 && dayInt !== 12) return 'nd'
	if (dayInt % 10 === 3 && dayInt !== 13) return 'rd'
	return 'th'
}

function getLastDayPercentageChange(weeklySadhnaCounts) {
	if (!weeklySadhnaCounts || weeklySadhnaCounts.length < 3) return null

	const current = weeklySadhnaCounts[weeklySadhnaCounts.length - 2]?.uniqueUserCount || 0
	const previous = weeklySadhnaCounts[weeklySadhnaCounts.length - 3]?.uniqueUserCount || 0

	if (previous === 0) return null

	const percentage = ((current - previous) / previous) * 100
	return {
		value: Math.abs(percentage).toFixed(1),
		isPositive: percentage >= 0,
		trend: percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral'
	}
}

function getTrendIcon(trend) {
	switch (trend) {
		case 'up':
			return <TrendingUpIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
		case 'down':
			return <TrendingDownIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
		default:
			return <TrendingFlatIcon sx={{ color: 'text.disabled', fontSize: '1.2rem' }} />
	}
}

export default function WeekelySadhnaBarChart({ weeklySadhnaCounts = [] }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const isTablet = useMediaQuery(theme.breakpoints.down('md'))

	// Enhanced data processing
	const processedData = React.useMemo(() => {
		if (!weeklySadhnaCounts || weeklySadhnaCounts.length === 0) return []

		return weeklySadhnaCounts.map((item, index) => ({
			...item,
			formattedDate: getFormattedDate(item.date, false),
			mobileDate: getFormattedDate(item.date, true),
			index
		}))
	}, [weeklySadhnaCounts])

	const xAxisData = processedData.map(item =>
		isMobile ? item.mobileDate : item.formattedDate
	)

	const colorPalette = [
		theme.palette.primary.light,
		theme.palette.primary.main,
		theme.palette.primary.dark,
	]

	// Calculate statistics
	const currentWeekData = processedData[processedData.length - 2]
	const previousWeekData = processedData[processedData.length - 3]
	const percentageChange = getLastDayPercentageChange(weeklySadhnaCounts)

	// Calculate average and total
	const totalUsers = processedData.reduce((sum, item) => sum + (item.uniqueUserCount || 0), 0)
	const averageUsers = processedData.length > 0 ? Math.round(totalUsers / processedData.length) : 0

	// Get current week range
	const getCurrentWeekRange = () => {
		if (processedData.length < 2) return 'This week'
		const current = processedData[processedData.length - 2]
		const startOfWeek = format(new Date(current.date), 'MMM d')
		const endOfWeek = format(new Date(current.date), 'MMM d, yyyy')
		return `${startOfWeek} - ${endOfWeek}`
	}

	if (processedData.length === 0) {
		return (
			<Card
				variant="outlined"
				sx={{
					width: '100%',
					borderRadius: { xs: '16px', sm: '12px' },
					boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', sm: '0 2px 8px rgba(0,0,0,0.06)' },
					border: 'none',
					background: theme.palette.mode === 'light'
						? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
						: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
				}}
			>
				<CardContent sx={{
					padding: { xs: 2, sm: 2.5 },
					textAlign: 'center',
					minHeight: '300px',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center'
				}}>
					<CalendarViewWeekIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
					<Typography variant="h6" color="text.secondary" gutterBottom>
						No Weekly Data Available
					</Typography>
					<Typography variant="body2" color="text.disabled">
						Weekly Sadhna data will appear here
					</Typography>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card
			variant="outlined"
			sx={{
				width: '100%',
				borderRadius: { xs: '16px', sm: '12px' },
				boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', sm: '0 2px 8px rgba(0,0,0,0.06)' },
				border: 'none',
				background: theme.palette.mode === 'light'
					? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
					: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
				transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
				position: 'relative',
				overflow: 'hidden',
				'&:hover': {
					transform: { xs: 'none', sm: 'translateY(-2px)' },
					boxShadow: { xs: '0 8px 30px rgba(0,0,0,0.12)', sm: '0 8px 25px rgba(0,0,0,0.1)' },
				},
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					height: '4px',
					background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
				}
			}}
		>
			<CardContent sx={{
				padding: { xs: 2, sm: 2.5 },
				'&:last-child': { paddingBottom: { xs: 2, sm: 2.5 } },
				position: 'relative'
			}}>
				{/* Header Section */}
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
					sx={{ mb: 3 }}
				>
					<Box sx={{ flex: 1 }}>
						<Typography
							component="h2"
							variant="h6"
							sx={{
								fontSize: { xs: '1.1rem', sm: '1.25rem' },
								fontWeight: 600,
								color: 'text.primary',
								lineHeight: 1.3,
								mb: 0.5,
								display: 'flex',
								alignItems: 'center',
								gap: 1
							}}
						>
							<CalendarViewWeekIcon sx={{ fontSize: '1.2em', color: 'secondary.main' }} />
							Weekly Sadhna Updates
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Track weekly spiritual engagement patterns
						</Typography>
					</Box>

					{/* Info Tooltip */}
					<Tooltip
						title="Weekly Sadhna tracking and user engagement analytics"
						placement="top"
						arrow
					>
						<IconButton
							size="small"
							sx={{
								color: 'text.disabled',
								'&:hover': {
									color: 'text.secondary',
									backgroundColor: 'action.hover'
								}
							}}
						>
							<InfoIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>

				{/* Statistics Section */}
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={{ xs: 2, sm: 3 }}
					sx={{ mb: 3 }}
				>
					{/* Current Week */}
					<Box sx={{
						flex: 1,
						textAlign: { xs: 'center', sm: 'left' }
					}}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent={{ xs: 'center', sm: 'flex-start' }}
							spacing={1}
							sx={{ mb: 1 }}
						>
							<Typography
								variant="h4"
								component="p"
								sx={{
									fontSize: { xs: '2rem', sm: '2.5rem' },
									fontWeight: 700,
									color: 'text.primary',
									lineHeight: 1.1
								}}
							>
								{currentWeekData?.uniqueUserCount || 0}
							</Typography>
							{percentageChange && (
								<Chip
									icon={getTrendIcon(percentageChange.trend)}
									size="small"
									color={percentageChange.isPositive ? 'success' : 'error'}
									label={`${percentageChange.isPositive ? '+' : '-'}${percentageChange.value}%`}
									variant="outlined"
									sx={{
										fontWeight: 600,
										fontSize: { xs: '0.75rem', sm: '0.8rem' },
										borderRadius: '20px',
										'& .MuiChip-icon': {
											fontSize: '1rem'
										}
									}}
								/>
							)}
						</Stack>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Current week
						</Typography>
					</Box>

					{/* Previous Week */}
					<Box sx={{
						flex: 1,
						textAlign: { xs: 'center', sm: 'left' }
					}}>
						<Typography
							variant="h5"
							component="p"
							sx={{
								fontSize: { xs: '1.5rem', sm: '2rem' },
								fontWeight: 600,
								color: 'text.secondary',
								lineHeight: 1.1,
								mb: 1
							}}
						>
							{previousWeekData?.uniqueUserCount || 0}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Previous week
						</Typography>
					</Box>

					{/* Average */}
					<Box sx={{
						flex: 1,
						textAlign: { xs: 'center', sm: 'left' }
					}}>
						<Typography
							variant="h5"
							component="p"
							sx={{
								fontSize: { xs: '1.5rem', sm: '2rem' },
								fontWeight: 600,
								color: 'text.secondary',
								lineHeight: 1.1,
								mb: 1
							}}
						>
							{averageUsers}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '0.875rem', sm: '1rem' }
							}}
						>
							Weekly average
						</Typography>
					</Box>
				</Stack>

				{/* Chart Section */}
				<Box sx={{
					width: '100%',
					height: { xs: '200px', sm: '250px', md: '300px' },
					position: 'relative'
				}}>
					<BarChart
						borderRadius={8}
						colors={[theme.palette.secondary.main]}
						xAxis={[
							{
								scaleType: 'band',
								categoryGapRatio: isMobile ? 0.3 : 0.5,
								data: xAxisData,
								tickLabelStyle: {
									fontSize: isMobile ? 10 : 12,
									fill: theme.palette.text.secondary,
									textAnchor: 'middle'
								}
							},
						]}
						yAxis={[
							{
								tickLabelStyle: {
									fontSize: isMobile ? 10 : 12,
									fill: theme.palette.text.secondary
								},
								grid: {
									strokeDasharray: '3 3',
									color: theme.palette.divider
								}
							}
						]}
						series={[
							{
								id: 'weeklySadhna',
								label: 'Weekly Sadhna Count',
								data: processedData.map(item => item.uniqueUserCount || 0),
								color: theme.palette.secondary.main,
							},
						]}
						height={isMobile ? 200 : isTablet ? 250 : 300}
						margin={{
							left: isMobile ? 40 : 50,
							right: isMobile ? 15 : 20,
							top: isMobile ? 15 : 20,
							bottom: isMobile ? 30 : 40
						}}
						grid={{
							horizontal: true,
							vertical: false,
							color: theme.palette.divider
						}}
						sx={{
							'& .MuiBarElement-root': {
								fill: theme.palette.secondary.main,
								'&:hover': {
									fill: theme.palette.secondary.dark,
									opacity: 0.8
								}
							},
							'& .MuiChartsAxis-line': {
								stroke: theme.palette.divider,
								strokeWidth: 1
							},
							'& .MuiChartsAxis-tick': {
								stroke: theme.palette.divider,
								strokeWidth: 1
							}
						}}
						slotProps={{
							legend: {
								hidden: true,
							},
							tooltip: {
								content: ({ active, payload }) => {
									if (active && payload && payload.length) {
										const data = payload[0].payload
										return (
											<Box sx={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 1,
												padding: 1,
												boxShadow: theme.shadows[4]
											}}>
												<Typography variant="body2" fontWeight="bold">
													{data.formattedDate || data.mobileDate}
												</Typography>
												<Typography variant="body2" color="secondary">
													{data.uniqueUserCount} unique users
												</Typography>
											</Box>
										)
									}
									return null
								}
							}
						}}
					/>
				</Box>

				{/* Chart Footer */}
				<Box sx={{
					mt: 2,
					textAlign: 'center'
				}}>
					<Typography
						variant="caption"
						sx={{
							color: 'text.disabled',
							fontSize: { xs: '0.75rem', sm: '0.8rem' }
						}}
					>
						{getCurrentWeekRange()} • {processedData.length} weeks of data • Total: {totalUsers} users
					</Typography>
				</Box>
			</CardContent>
		</Card>
	)
}

WeekelySadhnaBarChart.propTypes = {
	weeklySadhnaCounts: PropTypes.arrayOf(
		PropTypes.shape({
			date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
			uniqueUserCount: PropTypes.number.isRequired,
		})
	),
}
